import React, { useState, useEffect, useMemo } from 'react';
import { ArrowPathIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { mockParserApi } from '../services/mockParserApi';
import UploadPYQForm from '../components/mock-tests/UploadPYQForm';
import StatsCards from '../components/mock-tests/StatsCards';
import MockTestGenerator from '../components/mock-tests/MockTestGenerator';
import TestList from '../components/mock-tests/TestList';

export default function MockTests() {
  const [stats, setStats] = useState(null);
  const [pyqStats, setPyqStats] = useState(null);
  const [recentTests, setRecentTests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [paperFilter, setPaperFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingTests, setLoadingTests] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [activeTab, setActiveTab] = useState('generate'); // 'generate' or 'questions'

  // Load statistics
  const loadStats = async () => {
    setLoadingStats(true);
    try {
      // Load PYQ stats (uploaded PDFs) and Question Bank stats (AI-generated)
      const [pyqResponse, qbResponse] = await Promise.all([
        mockParserApi.getPYQStats(),
        mockParserApi.getQuestionBankStats(),
      ]);
      setPyqStats(pyqResponse.data);
      setStats(qbResponse.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  // Load recent tests
  const loadRecentTests = async () => {
    setLoadingTests(true);
    try {
      const response = await mockParserApi.getRecentTests(10);
      setRecentTests(response.data || []);
    } catch (error) {
      console.error('Error loading tests:', error);
      // Don't show error toast on initial load, might be empty
    } finally {
      setLoadingTests(false);
    }
  };

  // Load questions from question bank
  const loadQuestions = async (filters = {}) => {
    setLoadingQuestions(true);
    try {
      const response = await mockParserApi.getQuestions({ limit: 50, ...filters });
      setQuestions(response.data || []);
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadStats();
    loadRecentTests();
  }, []);

  // Load questions when switching to questions tab
  useEffect(() => {
    if (activeTab === 'questions') {
      loadQuestions();
    }
  }, [activeTab]);

  const subjectOptions = useMemo(() => {
    if (!stats?.bySubject) return [];
    return Object.keys(stats.bySubject).sort();
  }, [stats]);

  const paperOptions = useMemo(() => ['language', 'domain', 'general-aptitude'], []);
  const difficultyOptions = useMemo(() => ['easy', 'medium', 'hard'], []);

  const yearOptions = useMemo(() => {
    const set = new Set();
    questions.forEach((q) => {
      const year = q.sourceYear || (q.generatedAt ? new Date(q.generatedAt).getFullYear() : null);
      if (year) set.add(year);
    });
    const arr = Array.from(set).sort((a, b) => b - a);
    if (arr.length === 0) {
      const current = new Date().getFullYear();
      return [current, current - 1, current - 2];
    }
    return arr;
  }, [questions]);

  const applyFilters = () => {
    const payload = {};
    if (subjectFilter) payload.subject = subjectFilter;
    if (paperFilter) payload.paperType = paperFilter;
    if (difficultyFilter) payload.difficulty = difficultyFilter;
    if (yearFilter) payload.year = Number(yearFilter);
    if (dateFilter) payload.date = dateFilter;
    loadQuestions(payload);
  };

  const clearFilters = () => {
    setSubjectFilter('');
    setPaperFilter('');
    setDifficultyFilter('');
    setYearFilter('');
    setDateFilter('');
    loadQuestions();
  };

  // Handle successful upload
  const handleUploadSuccess = () => {
    loadStats();
    toast.success('Questions added to database');
  };

  // Handle successful test generation
  const handleGenerateSuccess = (test) => {
    loadRecentTests();
  };

  // Refresh all data
  const handleRefresh = () => {
    loadStats();
    loadRecentTests();
    toast.success('Data refreshed');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Mock Test Management
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Upload PYQ papers, generate mock tests, and manage your question bank
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Refresh</span>
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('generate')}
            className={`${activeTab === 'generate'
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Generate Mock Tests
          </button>
          <button
            onClick={() => setActiveTab('questions')}
            className={`${activeTab === 'questions'
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <AcademicCapIcon className="h-5 w-5 mr-2" />
            Question Bank ({stats?.totalQuestions || 0})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'generate' ? (
        <>
          {/* Statistics Cards */}
          <StatsCards stats={pyqStats} loading={loadingStats} />

          {/* Upload and Generate Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload PYQ Form */}
            <UploadPYQForm onUploadSuccess={handleUploadSuccess} />

            {/* Mock Test Generator */}
            <MockTestGenerator onGenerateSuccess={handleGenerateSuccess} />
          </div>

          {/* Recent Tests List */}
          <TestList
            tests={recentTests}
            loading={loadingTests}
            onRefresh={loadRecentTests}
          />
        </>
      ) : (
        <>
          {/* Question Bank */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">AI-Generated Question Bank</h2>
              <p className="mt-1 text-sm text-gray-600">
                Questions automatically generated daily at 7:00 AM from uploaded PYQ papers
              </p>
            </div>
            {/* Filters: paper, subject, difficulty, year */}
            <div className="px-6 py-4 bg-white flex flex-wrap items-center gap-3 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Paper</label>
                <select
                  className="ml-2 px-2 py-1 border rounded"
                  value={paperFilter}
                  onChange={(e) => setPaperFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {paperOptions.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Subject</label>
                <select
                  className="ml-2 px-2 py-1 border rounded min-w-[160px]"
                  value={subjectFilter}
                  onChange={(e) => setSubjectFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {subjectOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Difficulty</label>
                <select
                  className="ml-2 px-2 py-1 border rounded"
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {difficultyOptions.map((d) => (
                    <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Year</label>
                <select
                  className="ml-2 px-2 py-1 border rounded"
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Date</label>
                <input
                  type="date"
                  className="ml-2 px-2 py-1 border rounded"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                />
              </div>

              <div className="ml-auto flex items-center space-x-2">
                <button onClick={applyFilters} className="px-3 py-1 bg-indigo-600 text-white rounded">Apply</button>
                <button onClick={clearFilters} className="px-3 py-1 border rounded">Clear</button>
              </div>
            </div>

            {loadingQuestions ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : questions.length === 0 ? (
              <div className="text-center py-12">
                <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No questions yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Upload PYQ papers to start generating questions automatically.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Question
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Paper
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Difficulty
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Year
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {questions.map((q, idx) => (
                      <tr key={q._id || idx} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="max-w-2xl">
                            <p className="text-sm text-gray-900 mb-2">{q.questionText || q.question}</p>
                            {q.options && Array.isArray(q.options) && (
                              <div className="mt-2 space-y-1">
                                {q.options.map((option) => (
                                  <div
                                    key={option.key}
                                    className={`text-xs px-2 py-1 rounded ${option.key === q.correctAnswer
                                      ? 'bg-green-50 text-green-700 font-medium'
                                      : 'text-gray-600'
                                      }`}
                                  >
                                    <span className="font-semibold">{option.key}:</span> {option.text}
                                  </div>
                                ))}
                              </div>
                            )}

                            {(q.reasoning || q.explanation) && (
                              <div className="mt-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <p className="text-sm font-semibold text-blue-900 flex items-center gap-1">
                                  💡 Explanation:
                                </p>
                                <p className="text-sm text-blue-800 mt-1">{q.reasoning || q.explanation}</p>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {q.paperType || q.paper || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {q.subject || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${q.difficulty === 'easy'
                              ? 'bg-green-100 text-green-800'
                              : q.difficulty === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                              }`}
                          >
                            {q.difficulty ? q.difficulty.charAt(0).toUpperCase() + q.difficulty.slice(1) : 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {q.sourceYear || new Date(q.generatedAt).getFullYear()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
